<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import type { Package } from '@/api/lytAccountingClient'
import { usePackageMetadataQuery } from '@/composables/queries/usePackageMetadataQuery'
import { ArrowRight, Landmark } from 'lucide-vue-next'
import { Alert, Badge, Button, Card } from '@/components/ui'
import SetupFlowShell from '@/components/shared/SetupFlowShell.vue'
import { track } from '@/lib/usageTracker'
import { setupFlowSteps } from './setupFlow'

const router = useRouter()
const route = useRoute()
const store = useAuthStore()

const invoiceNumber = computed(() => route.query.invoiceNumber?.toString() || '')
// Accept both storeID and storeId query spellings used by intake links.
const storeID = computed(
  () => (route.query.storeID ?? route.query.storeId)?.toString() || '',
)

// Shared package-metadata query — same cache entry as PackageLogin (invoice + store).
const {
  data: packagesData,
  isLoading,
  error: queryError,
  refetch,
} = usePackageMetadataQuery({
  invoiceNumber,
  storeId: storeID,
})

const packages = computed(() => packagesData.value ?? [])
const loading = computed(() => isLoading.value)
const error = computed(() =>
  queryError.value ? 'Failed to load packages. Please try again.' : '',
)

const contextItems = computed(() => [
  { label: 'Invoice reference', value: invoiceNumber.value || 'Pending verification' },
  { label: 'Store reference', value: storeID.value || 'Pending verification' },
  {
    label: 'Provider shortlist',
    value: loading.value
      ? 'Loading eligible providers'
      : `${packages.value.length} option${packages.value.length === 1 ? '' : 's'} available`,
    tone: loading.value ? 'accent' : 'default',
  },
])

async function loadPackages() {
  track('btn:RefreshProviders', 'click')
  await refetch()
}

function selectPackage(pkg: Package) {
  track('btn:SelectProvider', 'click', { provider: pkg.id })
  if (!pkg || pkg.status !== 'ACTIVE') return
  store.selectPackage(pkg.id)
  router.push({ name: 'PackageLogin', params: { packageId: pkg.id }, query: route.query })
}

onMounted(() => {
  store.setQueryParams({ invoiceNumber: invoiceNumber.value, storeID: storeID.value })
})
</script>

<template>
  <SetupFlowShell
    eyebrow="Provider intake"
    title="Choose your accounting provider"
    subtitle="Eligible providers are tied to this invoice and store reference so you can continue setup without losing context."
    :current-step="2"
    :steps="setupFlowSteps"
    :context-items="contextItems"
  >
    <Card padding="lg" class="package-panel">
      <div class="package-panel__actions">
        <Button variant="secondary" @click="loadPackages" :loading="loading">Refresh providers</Button>
      </div>

      <Alert v-if="error" variant="error" title="Unable to load providers">
        {{ error }}
      </Alert>

      <div v-if="loading" class="package-grid">
        <Card v-for="index in 2" :key="index" padding="lg" class="provider-card provider-card--loading">
          <div class="provider-card__loading-block"></div>
          <div class="provider-card__loading-line provider-card__loading-line--wide"></div>
          <div class="provider-card__loading-line"></div>
        </Card>
      </div>

      <div v-else class="package-grid">
        <Card
          v-for="pkg in packages"
          :key="pkg.id"
          padding="lg"
          class="provider-card"
          :class="{ 'provider-card--disabled': pkg.status !== 'ACTIVE', 'provider-card--active': pkg.status === 'ACTIVE' }"
          :role="pkg.status === 'ACTIVE' ? 'button' : undefined"
          :tabindex="pkg.status === 'ACTIVE' ? 0 : -1"
          @click="selectPackage(pkg)"
          @keydown.enter.prevent="selectPackage(pkg)"
          @keydown.space.prevent="selectPackage(pkg)"
        >
          <div class="provider-card__badge-row">
            <Badge
              size="sm"
              class="provider-card__badge"
              :variant="pkg.status === 'ACTIVE' ? 'success' : 'default'"
            >
              {{ pkg.status === 'ACTIVE' ? 'Ready now' : 'Coming soon' }}
            </Badge>
          </div>

          <div class="provider-card__head">
            <div class="provider-card__identity">
              <div class="provider-card__mark">
                <img v-if="pkg.imageUrl" :src="pkg.imageUrl" :alt="`${pkg.name} logo`" loading="lazy" />
                <Landmark v-else class="h-5 w-5" />
              </div>
              <div>
                <div class="provider-card__name">{{ pkg.name }}</div>
                <div class="provider-card__meta">Accounting provider</div>
              </div>
            </div>
          </div>

          <p class="provider-card__description">{{ pkg.description || 'Connect this provider to begin syncing accounting records from your store.' }}</p>

          <div class="provider-card__readiness">
            <span class="provider-card__readiness-label">Readiness</span>
            <span class="provider-card__readiness-value">
              {{ pkg.status === 'ACTIVE' ? 'Eligible for this invoice' : 'Not yet available for selection' }}
            </span>
          </div>

          <div class="provider-card__action-row">
            <Button
              variant="secondary"
              :disabled="pkg.status !== 'ACTIVE'"
              @click.stop="selectPackage(pkg)"
            >
              Continue
              <ArrowRight class="h-4 w-4" />
            </Button>
          </div>
        </Card>
      </div>
    </Card>
  </SetupFlowShell>
</template>

<style scoped>
.package-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.package-panel__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.provider-card__readiness-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-ink-soft);
}
.package-grid {
  display: grid;
  gap: 0.9rem;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
}

.provider-card {
  display: flex;
  flex-direction: column;
  gap: 0.95rem;
  min-height: 250px;
}

.provider-card--active {
  cursor: pointer;
  transition: border-color 0.18s ease, background-color 0.18s ease, transform 0.18s ease;
}

.provider-card--active:hover {
  border-color: var(--color-border-strong);
  background: rgba(255, 255, 255, 0.98);
  transform: translateY(-1px);
}

.provider-card--active:focus-visible {
  outline: 2px solid rgba(79, 70, 229, 0.22);
  outline-offset: 2px;
}

.provider-card--disabled {
  opacity: 0.72;
}

.provider-card__badge-row,
.provider-card__head,
.provider-card__identity,
.provider-card__action-row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
}

.provider-card__badge-row {
  justify-content: flex-end;
}

.provider-card__badge {
  padding-inline: 0.45rem;
  min-height: 1.35rem;
}

.provider-card__identity {
  justify-content: flex-start;
  align-items: center;
}

.provider-card__mark {
  width: 3rem;
  height: 3rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-soft);
  background: var(--color-surface-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-ink);
  overflow: hidden;
}

.provider-card__mark img {
  max-width: 72%;
  max-height: 72%;
  object-fit: contain;
}

.provider-card__name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-ink);
}

.provider-card__meta {
  font-size: 0.8rem;
  color: var(--color-ink-soft);
}

.provider-card__description {
  margin: 0;
  color: var(--color-ink-soft);
  font-size: 0.88rem;
}

.provider-card__action-row {
  margin-top: auto;
  justify-content: flex-start;
}

.provider-card__readiness {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.provider-card__readiness-value {
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--color-ink);
  max-width: 22ch;
}

.provider-card--loading {
  pointer-events: none;
}

.provider-card__loading-block,
.provider-card__loading-line {
  border-radius: var(--radius-sm);
  background: linear-gradient(90deg, rgba(21, 32, 51, 0.06), rgba(21, 32, 51, 0.12), rgba(21, 32, 51, 0.06));
  background-size: 200% 100%;
  animation: shimmer 1.2s linear infinite;
}

.provider-card__loading-block {
  width: 3rem;
  height: 3rem;
}

.provider-card__loading-line {
  height: 0.8rem;
}

.provider-card__loading-line--wide {
  width: 70%;
 }

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (max-width: 640px) {
  .package-panel {
    gap: 0.75rem;
  }

  .provider-card__head {
    flex-direction: column;
    align-items: flex-start;
  }

  .package-grid {
    grid-template-columns: 1fr;
    gap: 0.7rem;
  }

  .provider-card {
    min-height: 0;
    gap: 0.75rem;
  }

  .provider-card__badge-row {
    justify-content: flex-start;
  }

  .provider-card__mark {
    width: 2.65rem;
    height: 2.65rem;
  }

  .provider-card__name {
    font-size: 0.95rem;
  }

  .provider-card__description {
    font-size: 0.84rem;
  }

  .provider-card__readiness-value {
    max-width: none;
  }

  .provider-card__action-row {
    width: 100%;
  }

  .provider-card__action-row :deep(button) {
    width: 100%;
    justify-content: center;
  }
}
</style>
